<template>
  <div class="PrognosePanel d-flex">
    <aside class="PrognosePanel__Aside flex-shrink-0 mr-3">
      <h3 class="PrognosePanel__Title">
        {{ $t("featureForecastPanel.forecastPanel.title") }}
      </h3>
      <div class="w-75 mt-3">
        <!-- TODO:: add description? -->
      </div>
    </aside>

    <div class="PrognosePanel__Main flex-grow-1 flex-shrink-1">
      <div class="d-flex align-items-center">
        <span class="PrognosePanel__Label mr-3">
          {{ $t("featureForecastPanel.forecastPanel.standardYear.title") }}
        </span>
        <div class="flex-grow-1 mr-3">
          <vSelect
            v-model="data.defaultYear"
            :options="data.years"
          />
        </div>
        <span id="defaultYear">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>

        <b-tooltip
          target="defaultYear"
          triggers="hover"
        >
          {{ $t("featureForecastPanel.forecastPanel.standardYear.tooltip") }}
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-4">
        <span class="PrognosePanel__Label mr-3">
          {{ $t("featureForecastPanel.forecastPanel.scenarioYears.title") }}
        </span>
        <vSelect
          v-model="data.years"
          class="flex-grow-1 mr-3"
          multiple
          :options="options"
        />
        <span id="scenarioYears">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>

        <b-tooltip
          target="scenarioYears"
          triggers="hover"
        >
          {{ $t("featureForecastPanel.forecastPanel.scenarioYears.tooltip") }}
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-4">
        <span class="PrognosePanel__Label mr-3">
          {{ $t("featureForecastPanel.forecastPanel.defaultLayer.title") }}
        </span>
        <div class="flex-grow-1 mr-3">
          <vSelect
            v-model="data.defaultLayer"
            :options="layerOptions"
            :reduce="option => option.value"
          />
        </div>
        <span id="defaultLayer">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>

        <b-tooltip
          target="defaultLayer"
          triggers="hover"
        >
          {{ $t("featureForecastPanel.forecastPanel.defaultLayer.tooltip") }}
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-3">
        <span class="PrognosePanel__Label mr-3" />
        <ToggleSwitch
          :value="data.onlyDefaultLayer"
          @input="value => data.onlyDefaultLayer = value"
        />
        <span class="ml-3">
          {{ $t("featureForecastPanel.forecastPanel.onlyDefaultLayer.title") }}
        </span>
        <span
          id="onlyDefaultLayer"
          class="ml-3"
        >
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>
        <b-tooltip
          target="onlyDefaultLayer"
          triggers="hover"
        >
          {{ $t("featureForecastPanel.forecastPanel.onlyDefaultLayer.tooltip") }}
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-4">
        <span class="PrognosePanel__Label mr-3">
          {{ $t("featureForecastPanel.forecastPanel.scenarioModels.title") }}
        </span>
        <vSelect
          v-model="data.models"
          class="flex-grow-1 mr-3"
          multiple
          :options="modelOptions"
        />
        <span id="scenarioModels">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>

        <b-tooltip
          target="scenarioModels"
          triggers="hover"
        >
          {{ $t("featureForecastPanel.forecastPanel.scenarioModels.tooltip") }}
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-4">
        <span class="PrognosePanel__Label mr-3">
          {{ $t("featureForecastPanel.forecastPanel.evLimit.title") }}
        </span>
        <b-input
          v-model="data.limit"
          type="number"
        />
        <span class="flex-grow-1" />
        <span id="evLimit">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>
        <b-tooltip
          target="evLimit"
          triggers="hover"
        >
          {{ $t("featureForecastPanel.forecastPanel.evLimit.tooltip") }}
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-4">
        <span class="PrognosePanel__Label mr-3">
          {{ $t("featureForecastPanel.forecastPanel.legend.title") }}
        </span>

        <div class="PrognosePanel__Breakpoints d-flex flex-wrap flex-shrink-1">
          <span class="d-flex align-items-center mb-3">
            <span class="flex-shrink-0 mr-3">0</span>
            <span class="flex-shrink-0 mr-3">{{ $t('to') }}</span>
            <b-input
              v-model="data.breakpoint_one"
              type="number"
              class="mr-3"
            />
            <span class="flex-shrink-0 mr-3"> {{ $t('to') }}</span>
            <b-input
              v-model="data.breakpoint_two"
              type="number"
              class="mr-3"
            />
          </span>
          <span class="d-flex align-items-center mb-3">
            <span class="Breakpoints__Repeat flex-shrink-0 mr-3">
              {{ data.breakpoint_two }}
            </span>
            <span class="flex-shrink-0 mr-3"> {{ $t('to') }}</span>
            <b-input
              v-model="data.breakpoint_three"
              type="number"
              class="mr-3"
            />
            <span class="flex-shrink-0 mr-2"> +</span>
          </span>
        </div>

        <span class="flex-grow-1" />
        <span id="breakpoints">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>
        <b-tooltip
          target="breakpoints"
          triggers="hover"
        >
          {{ $t("featureForecastPanel.forecastPanel.legend.tooltip") }}
        </b-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from '@/components/form/vSelect'
import ToggleSwitch from '@/components/form/ToggleSwitch'
import { mapGetters } from 'vuex'

export default {
  name: 'PrognosePanel',
  components: {
    vSelect,
    ToggleSwitch,
  },
  props: {
    /**
     * The data model values
     */
    values: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      options: [],

      // The data models
      data: {
        enabled: true,
        years: [],
        defaultYear: null,
        defaultLayer: null,
        onlyDefaultLayer: false,
        models: [],
        limit: 0,
        breakpoint_one: null,
        breakpoint_two: null,
        breakpoint_three: null,
      },

      trackChanges: true,
    }
  },
  computed: {
    ...mapGetters('deployment', ['DeploymentCode', 'DeploymentHasLayer']),
    ...mapGetters('deployment', { modelOptions: 'getPrognoseModels' }),
    ...mapGetters('layers', ['getForecastLayerDetails']),
    layerOptions() {
      return this.getForecastLayerDetails
        .filter(layer => this.DeploymentHasLayer({ layer: layer.id }))
        .map(layer => ({
          value: layer.id,
          label: this.$t(layer.label),
        }))
    },
  },
  watch: {
    /**
     * Values passed via props override the data, without signaling input changes
     */
    values() {
      this.setValues()
    },
    /**
     * When the default layer changes interactively, sync the scenario years
     * to whatever that layer declares as `availableYears`, clearing the rest.
     */
    'data.defaultLayer'(layerId) {
      if (!this.trackChanges || !layerId) return
      const layer = this.getForecastLayerDetails.find(l => l.id === layerId)
      const availableYears = layer?.config?.availableYears
      if (!availableYears?.length) return

      this.data.years = [...availableYears]
      if (!availableYears.includes(this.data.defaultYear)) {
        this.data.defaultYear = availableYears[0]
      }
    },
    /**
     * When any input changes, pass it on
     */
    data: {
      handler(data) {
        if (this.trackChanges) {
          this.$emit('input', data)
        }
      },
      deep: true,
    },
  },
  created() {
    this.setValues()
  },
  methods: {
    /**
     * Set prop values as new data, without signaling input changes
     */
    setValues() {
      this.trackChanges = false
      this.options = this.generateYears(2020, 2050),
      this.data = {
        ...this.values,
        defaultYear: this.values.defaultYear || this.values.years?.[0] || null,
        defaultLayer: this.values.defaultLayer ?? null,
        onlyDefaultLayer: this.values.onlyDefaultLayer ?? false,
      }

      this.$nextTick(() => {
        this.trackChanges = true
      })
    },
    generateYears(start, end, skip=1){
      const years = []

      for (let i = start; i <= end; i+=skip){
        years.push(i)
      }

      return years
    },
  },
}
</script>

<style lang="scss">
.PrognosePanel {
  &__Aside {
    width: 200px;

    @media (min-width: 1280px) {
      width: 275px;
    }
  }
  &__Main {
    font-size: 1.25rem;

    .form-control {
      width: 100px;
      font-size: 1.25rem;
    }
  }
  &__Breakpoints {
    .form-control {
      width: 100px;
    }
    .Breakpoints__Repeat {
      @media (min-width: 1410px) {
        display: none;
      }
    }
  }
  &__Label {
    flex-shrink: 0;
    width: 175px;
  }
  .mb-3 {
    margin-bottom: 0 !important;
  }
}
</style>
